<template>
  <div class="pricing-page">
    <!-- Header Section -->
    <section class="pricing-header">
      <div class="container">
        <div class="header-content animate__animated animate__fadeInUp">
          <h1>Flexible Pricing Plans</h1>
          <p v-if="personaDoor">{{ $t(`pricing.doorNote.${personaDoor.id}`) }}</p>
          <p v-else>Choose the development environment that fits your team's needs and scale</p>
          <div class="pricing-features">
            <div class="feature-highlight">
              <i class="fas fa-clock"/>
              <span>2-minute provisioning</span>
            </div>
            <div class="feature-highlight">
              <i class="fas fa-shield-alt"/>
              <span>Enterprise security</span>
            </div>
            <div class="feature-highlight">
              <i class="fas fa-headset"/>
              <span>24/7 support included</span>
            </div>
          </div>

          <!-- Currency Selector -->
          <div class="currency-selector" v-if="!loading && !loadError">
            <span class="currency-label">Show prices in</span>
            <div class="currency-toggle">
              <button
                v-for="cur in availableCurrencies"
                :key="cur"
                class="currency-btn"
                :class="{ active: cur === currency }"
                @click="setCurrency(cur)"
                type="button"
              >
                {{ cur }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Cards -->
    <section class="pricing-section">
      <div class="container">
        <!-- Loading skeletons -->
        <div v-if="loading" class="pricing-grid">
          <div v-for="n in 3" :key="`sk-${n}`" class="pricing-card skeleton-card">
            <div class="skeleton-header"/>
            <div class="skeleton-line"/>
            <div class="skeleton-price"/>
            <div class="skeleton-line"/>
            <div class="skeleton-line short"/>
            <div class="skeleton-line"/>
            <div class="skeleton-button"/>
          </div>
        </div>

        <!-- Error state -->
        <div v-else-if="loadError" class="pricing-error animate__animated animate__fadeIn">
          <i class="fas fa-exclamation-triangle"/>
          <h3>We couldn't load pricing right now</h3>
          <p>The plans service is temporarily unavailable. Please try again in a moment.</p>
          <button class="btn btn-primary" @click="loadPlans">
            <i class="fas fa-sync"/>
            Retry
          </button>
        </div>

        <!-- Self-managed door: a licence, not a plan — no cards, the
             enterprise block below carries the conversation. -->
        <div v-else-if="licenceOnly" class="pricing-licence-only">
          <i class="fas fa-key"/>
          <h3>{{ $t('doors.self-managed.licenceBlock.title') }}</h3>
          <p>{{ $t('doors.self-managed.licenceBlock.body') }}</p>
          <router-link to="/for/self-managed" class="btn btn-primary">
            <i class="fas fa-door-open"/>
            {{ $t('pricing.enterpriseSelfManaged') }}
          </router-link>
        </div>

        <!-- Live plan cards (door-aware: the door's emphasised plans lead,
             the rest sit behind "Need more?" — every plan one click away) -->
        <div v-else class="pricing-grid">
          <div
            v-for="(plan, index) in visiblePlans"
            :key="plan.id"
            class="pricing-card animate__animated animate__fadeInUp"
            :class="{
              'featured': isFeatured(plan),
              'tier-membership': plan.tier_type === 'membership',
              'tier-workspace': plan.tier_type === 'workspace'
            }"
            :style="{ animationDelay: `${index * 0.08}s` }"
          >
            <div class="plan-header">
              <span class="plan-tier-tag">{{ tierLabel(plan) }}</span>
              <span v-if="doorTag(plan)" class="plan-door-tag">{{ doorTag(plan) }}</span>
              <h3>{{ plan.name }}</h3>
              <div class="plan-price">
                <template v-if="priceFor(plan)">
                  <span class="currency">{{ currencySymbol }}</span>
                  <span class="amount">{{ formatAmount(priceFor(plan).price) }}</span>
                  <span class="period">/{{ billingLabel(plan) }}</span>
                </template>
                <template v-else>
                  <span class="amount-na">Pricing on request</span>
                </template>
                <div v-if="priceFor(plan)?.discount_percent > 0" class="plan-discount">
                  Save {{ priceFor(plan).discount_percent }}%
                </div>
              </div>
              <p class="plan-description">{{ plan.description }}</p>
              <div v-if="isFeatured(plan)" class="featured-badge">
                <i class="fas fa-star"/>
                {{ doorSplit ? $t('pricing.recommended') : 'Most Popular' }}
              </div>
            </div>

            <div class="plan-features">
              <h4>Resources</h4>
              <ul class="feature-list">
                <li>
                  <i class="fas fa-microchip"/>
                  <span>{{ plan.cpu }} vCPU</span>
                </li>
                <li>
                  <i class="fas fa-memory"/>
                  <span>{{ plan.ram_gb }} GB RAM</span>
                </li>
                <li>
                  <i class="fas fa-hdd"/>
                  <span>{{ plan.storage_gb }} GB storage</span>
                </li>
              </ul>

              <template v-if="hasWorkloadCaps(plan)">
                <h4>Per-workload caps</h4>
                <ul class="feature-list">
                  <li v-if="plan.max_workload_cpu">
                    <i class="fas fa-check"/>
                    <span>{{ plan.max_workload_cpu }} vCPU per workload</span>
                  </li>
                  <li v-if="plan.max_workload_ram_gb">
                    <i class="fas fa-check"/>
                    <span>{{ plan.max_workload_ram_gb }} GB RAM per workload</span>
                  </li>
                  <li v-if="plan.max_workload_storage_gb">
                    <i class="fas fa-check"/>
                    <span>{{ plan.max_workload_storage_gb }} GB storage per workload</span>
                  </li>
                </ul>
              </template>
            </div>

            <div class="plan-action">
              <button
                class="btn"
                :class="isFeatured(plan) ? 'btn-primary' : 'btn-outline'"
                @click="selectPlan(plan)"
              >
                <i :class="plan.tier_type === 'membership' ? 'fas fa-user-plus' : 'fas fa-rocket'"/>
                {{ ctaLabel(plan) }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="!loading && !loadError && !licenceOnly && collapsedPlans.length" class="pricing-need-more">
          <button type="button"
class="btn btn-outline"
:aria-expanded="showAll ? 'true' : 'false'"
@click="showAll = !showAll">
            <i :class="showAll ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"/>
            {{ showAll ? 'Show fewer plans' : $t('doors.common.needMore') }}
            <span v-if="!showAll" class="need-more-names">{{ collapsedPlans.map((p) => p.name).join(', ') }}</span>
          </button>
        </div>
        <p v-if="personaDoor && personaDoor.id === 'developers'" class="pricing-door-note">
          {{ $t('pricing.doorNote.developers') }}
        </p>
      </div>
    </section>

    <!-- Enterprise Section (kept as static marketing copy) -->
    <section class="enterprise-section">
      <div class="container">
        <div class="enterprise-content animate__animated animate__fadeInUp">
          <div class="enterprise-info">
            <h2>Enterprise Solutions</h2>
            <p>Need custom configurations, dedicated infrastructure, or special compliance requirements?</p>

            <div class="enterprise-features">
              <div class="enterprise-feature">
                <i class="fas fa-server"/>
                <div>
                  <h4>Dedicated Infrastructure</h4>
                  <p>Private VMware clusters with dedicated GPU resources</p>
                </div>
              </div>

              <div class="enterprise-feature">
                <i class="fas fa-shield-alt"/>
                <div>
                  <h4>Custom Security</h4>
                  <p>Government-grade security with custom compliance</p>
                </div>
              </div>

              <div class="enterprise-feature">
                <i class="fas fa-headset"/>
                <div>
                  <h4>Priority Support</h4>
                  <p>Dedicated technical account manager and SLA</p>
                </div>
              </div>
            </div>
          </div>

          <div class="enterprise-action">
            <div class="contact-card">
              <h3>Let's discuss your needs</h3>
              <p>Schedule a consultation with our technical team</p>
              <router-link to="/contact" class="btn btn-primary">
                <i class="fas fa-calendar"/>
                Schedule Demo
              </router-link>
              <router-link to="/for/self-managed" class="enterprise-self-managed">
                {{ $t('pricing.enterpriseSelfManaged') }} <i class="fas fa-arrow-right"/>
              </router-link>
              <div class="contact-info">
                <p><i class="fas fa-envelope"/> enterprise@caucasus.systems</p>
                <p><i class="fas fa-phone"/> Available 24/7</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="faq-section">
      <div class="container">
        <div class="section-header animate__animated animate__fadeInUp">
          <h2>Frequently Asked Questions</h2>
          <p>Everything you need to know about our pricing and plans</p>
        </div>

        <div class="faq-grid">
          <div
            v-for="(faq, index) in faqs"
            :key="index"
            class="faq-item animate__animated animate__fadeInUp"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div class="faq-question" @click="toggleFaq(index)">
              <h4>{{ faq.question }}</h4>
              <i class="fas fa-chevron-down" :class="{ 'rotated': faq.open }"/>
            </div>
            <div class="faq-answer" v-show="faq.open">
              <!-- W1: the first FAQ ("Can I start free?") renders the live
                   trial periods from the plan's trial block when available;
                   every other FAQ uses its static answer. -->
              <p>{{ index === 0 ? trialFaqAnswer : faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { clusterApi } from '@/services/cluster-api'
import { usePersona } from '@/utils/persona'
import {
  loadPlansCached, emphasisePlans, tierKeyOf,
  CURRENCY_STORAGE_KEY, SUPPORTED_CURRENCIES, CURRENCY_SYMBOLS
} from '@/utils/plans-source'

export default {
  name: 'Pricing',
  setup() {
    // Persona door → default emphasis (design doc §6 "Plan shown").
    const { door: personaDoor } = usePersona()
    return { personaDoor }
  },
  data() {
    return {
      // "Need more?" expander for the plans a door collapses.
      showAll: false,
      plans: [],
      loading: true,
      loadError: false,
      currency: 'EUR',
      // W1 — live trial terms from the trial-enabled plan's `trial` block
      // (GET /api/cluster/plans, or the thin /plans/trial-terms endpoint).
      // Null until loaded (or when the backend hasn't shipped the trial block
      // yet) — in that case the trial FAQ falls back to the generic static
      // copy below. See `trialFaqAnswer`.
      trialTerms: null,
      faqs: [
        {
          question: 'Can I start free?',
          // GENERIC FALLBACK copy. When `trialTerms` loads, the rendered
          // answer comes from `trialFaqAnswer` (computed) instead of this
          // literal — so editing the plan in the admin UI changes the page
          // with no website deploy (W1 acceptance criterion).
          answer: 'Yes — the Starter membership is free and gives you a Caucasus Systems Basic workspace to try the platform. Upgrade to a Pro or Business workspace at any time.',
          open: false
        },
        {
          question: 'Can I upgrade or downgrade my plan anytime?',
          answer: 'Yes. Upgrades take effect immediately on the next provisioning cycle. Downgrades take effect at the next billing period with prorated refunds where applicable.',
          open: false
        },
        {
          question: 'Which currencies do you support?',
          answer: 'We bill in EUR, USD, and GEL. Use the currency switcher above to compare prices in your preferred currency before checkout.',
          open: false
        },
        {
          question: 'How does activation work?',
          answer: 'When you buy a Pro or Business workspace we create your organization in pending state. The Caucasus Systems team activates it shortly after we confirm payment, and you get an email when your workspace is live.',
          open: false
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards (Visa, MasterCard, Amex), bank transfers for annual plans, and wire transfers for enterprise customers.',
          open: false
        },
        {
          question: 'Is my data secure and private?',
          answer: 'Absolutely. We use enterprise-grade encryption, comply with SOC 2 standards, and your data never leaves our secure infrastructure. All plans include encryption at rest and in transit.',
          open: false
        },
        {
          question: 'Do you offer custom enterprise solutions?',
          answer: 'Yes — dedicated infrastructure, white-label options, specialized compliance (HIPAA, SOC2, ISO), and tailored SLAs. Contact our enterprise team to discuss your requirements.',
          open: false
        },
        {
          question: 'What happens if I exceed my resource limits?',
          answer: 'We provide soft limits with warnings first. You can either upgrade your plan or pay overage fees. We will never shut down your services without notice.',
          open: false
        }
      ]
    }
  },
  computed: {
    /** Door E shows no price cards at all. */
    licenceOnly() {
      return Boolean(this.personaDoor && this.personaDoor.plans.length === 0)
    },
    doorSplit() {
      if (!this.personaDoor || !this.personaDoor.plans.length) return null
      return emphasisePlans(this.plans, this.personaDoor.plans)
    },
    visiblePlans() {
      if (!this.doorSplit || this.showAll) return this.plans
      return this.doorSplit.emphasised
    },
    collapsedPlans() {
      return this.doorSplit ? this.doorSplit.collapsed : []
    },
    availableCurrencies() {
      // Show only currencies that at least one plan actually prices in.
      const seen = new Set()
      this.plans.forEach((plan) => {
        (plan.prices || []).forEach((p) => seen.add(p.currency))
      })
      // Preserve our preferred ordering (EUR, USD, GEL) but only include
      // currencies that are present.
      const ordered = SUPPORTED_CURRENCIES.filter((c) => seen.has(c))
      return ordered.length > 0 ? ordered : SUPPORTED_CURRENCIES
    },
    currencySymbol() {
      return CURRENCY_SYMBOLS[this.currency] || this.currency
    },
    /**
     * W1 — the answer to the "Can I start free?" FAQ.
     *
     * When the trial-enabled plan exposes a `trial` block we compose the
     * sentence from its live periods so a super-admin edit reflects with no
     * website deploy. We never hardcode the period literals. When the trial
     * block isn't present (backend not shipped yet, or trial disabled), we
     * fall back to the generic static answer (faqs[0].answer).
     */
    trialFaqAnswer() {
      const t = this.trialTerms
      if (t && t.enabled !== false && (t.trial_period_human || t.grace_period_human)) {
        const trialPeriod = t.trial_period_human || 'a limited time'
        const grace = t.grace_period_human
        const warn = t.predelete_warn_human
        let sentence = `Yes — start free with no card. Your free trial lasts ${trialPeriod}`
        if (grace) {
          sentence += `; after it ends your data is kept for ${grace} before deletion`
          if (warn) {
            sentence += `, and we send a warning ${warn} beforehand`
          }
        }
        sentence += '. Upgrade to a paid workspace any time to keep everything running.'
        return sentence
      }
      // Graceful fallback to the generic copy.
      return this.faqs[0].answer
    }
  },
  mounted() {
    // Restore user's currency choice if they've picked one before.
    try {
      const saved = localStorage.getItem(CURRENCY_STORAGE_KEY)
      if (saved && SUPPORTED_CURRENCIES.includes(saved)) {
        this.currency = saved
      }
    } catch (_) {
      // localStorage may be unavailable in private browsing — silently fall
      // back to the default.
    }
    this.loadPlans()
  },
  methods: {
    async loadPlans() {
      this.loading = true
      this.loadError = false
      try {
        const data = await loadPlansCached(() => clusterApi.getPlans())
        // Sort by tier_type (membership first) then by primary price asc so
        // the cheapest workspace shows up next to the free starter.
        const sorted = (data || []).slice().sort((a, b) => {
          if (a.tier_type !== b.tier_type) {
            return a.tier_type === 'membership' ? -1 : 1
          }
          const ap = this.priceFor(a)?.price || 0
          const bp = this.priceFor(b)?.price || 0
          return ap - bp
        })
        this.plans = sorted

        // W1 — resolve the live trial terms. Pass the already-fetched plans
        // so the plans-list fallback path doesn't issue a second request.
        // Degrades to null (→ static FAQ fallback) when the backend hasn't
        // shipped the trial block / thin endpoint yet.
        try {
          this.trialTerms = await clusterApi.getTrialTerms(data)
        } catch (_) {
          this.trialTerms = null
        }
      } catch (error) {
        console.error('Failed to load plans:', error)
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    setCurrency(cur) {
      this.currency = cur
      try {
        localStorage.setItem(CURRENCY_STORAGE_KEY, cur)
      } catch (_) {
        // ignore — selection is still applied for the current session
      }
    },
    priceFor(plan) {
      if (!plan?.prices?.length) return null
      // Prefer exact-currency match; fall back to the first listed price so
      // we always render *something* (the UI still labels with the requested
      // currency switcher, but at least the plan card is informative).
      return (
        plan.prices.find((p) => p.currency === this.currency) ||
        plan.prices[0] ||
        null
      )
    },
    formatAmount(price) {
      if (price === null || price === undefined) return '—'
      const num = Number(price)
      if (!Number.isFinite(num)) return String(price)
      // Drop trailing .00 for clean display, keep precision otherwise.
      return Number.isInteger(num) ? num.toLocaleString() : num.toFixed(2)
    },
    billingLabel(plan) {
      const period = (plan.billing_period || 'month').toLowerCase()
      if (period === 'year' || period === 'annual' || period === 'yearly') return 'year'
      if (period === 'month' || period === 'monthly') return 'month'
      return period
    },
    tierLabel(plan) {
      if (plan.tier_type === 'membership') return 'Free starter'
      if (plan.tier_type === 'workspace') return 'Workspace'
      return plan.tier_type || 'Plan'
    },
    /** Door-specific tag beside the tier label (design doc §6). */
    doorTag(plan) {
      const d = this.personaDoor
      if (!d) return ''
      const key = tierKeyOf(plan)
      if (d.id === 'agencies' && key === 'professional') return this.$t('pricing.growingAgency')
      if (d.id === 'engineering-leads' && key === 'team') return this.$t('pricing.entry')
      return ''
    },
    isFeatured(plan) {
      // With a door chosen, its leading plan is featured.
      if (this.doorSplit && this.doorSplit.lead) return this.doorSplit.lead.id === plan.id
      // Otherwise the first workspace plan. With the sorted order
      // (memberships first, then cheapest workspace), this lands on the
      // entry-level Pro plan most of the time.
      if (plan.tier_type !== 'workspace') return false
      const firstWorkspace = this.plans.find((p) => p.tier_type === 'workspace')
      return firstWorkspace && firstWorkspace.id === plan.id
    },
    hasWorkloadCaps(plan) {
      return Boolean(
        plan.max_workload_cpu ||
        plan.max_workload_ram_gb ||
        plan.max_workload_storage_gb
      )
    },
    ctaLabel(plan) {
      if (plan.tier_type === 'membership') return 'Sign up — Free starter'
      return 'Buy / Get started'
    },
    selectPlan(plan) {
      if (plan.tier_type === 'membership') {
        // Free starter goes through the existing register modal.
        this.$store.commit('setShowAuthModal', true)
        this.$store.commit('setAuthModalType', 'register')
        return
      }
      // Workspace plans go to the buy wizard with the selected currency.
      this.$router.push({
        path: '/buy',
        query: { plan_id: plan.id, currency: this.currency }
      })
    },
    toggleFaq(index) {
      this.faqs[index].open = !this.faqs[index].open
    }
  }
}
</script>

<style scoped>
/* Door-aware emphasis */
.plan-door-tag {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: var(--ds-accent-light);
  color: var(--ds-primary-900);
  font-size: 0.72rem;
  font-weight: 600;
}
.pricing-need-more { text-align: center; margin-top: 2rem; }
.pricing-need-more .btn { max-width: 100%; flex-wrap: wrap; }
.need-more-names { color: var(--ds-neutral-500); font-weight: 400; font-size: 0.85rem; }
.pricing-door-note { text-align: center; color: var(--ds-neutral-500); margin: 1.5rem auto 0; max-width: 60ch; }
.pricing-licence-only {
  max-width: 640px;
  margin: 0 auto;
  background: var(--ds-white);
  border: 1px solid var(--ds-neutral-200);
  border-left: 6px solid var(--ds-primary-900);
  border-radius: var(--ds-radius-lg);
  padding: 2rem;
}
.pricing-licence-only > i { color: var(--ds-primary-500); font-size: 1.5rem; margin-bottom: 0.75rem; display: block; }
.pricing-licence-only h3 { margin: 0 0 0.5rem; font-family: var(--ds-font-display); color: var(--ds-primary-900); }
.pricing-licence-only p { margin: 0 0 1.25rem; color: var(--ds-neutral-700); line-height: 1.6; }
.enterprise-self-managed { display: inline-flex; gap: 0.5rem; align-items: center; margin-top: 1rem; color: var(--ds-primary-500); font-weight: 600; text-decoration: none; }
.enterprise-self-managed:hover { text-decoration: underline; }

.pricing-page {
  min-height: 100vh;
  background: #f9fafb;
  padding-top: 80px;
}

/* Header Section */
.pricing-header {
  padding: 80px 0;
  text-align: center;
  color: white;
  background: #0a2540;
  background-image: url('/media/recraft/textures/texture-grid.svg');
  background-size: cover;
  background-position: center;
  position: relative;
}

.pricing-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.92) 0%, rgba(26, 127, 212, 0.6) 100%);
  z-index: 0;
}

.pricing-header .container {
  position: relative;
  z-index: 1;
}

.pricing-header h1 {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.pricing-header p {
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.pricing-features {
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
}

.feature-highlight {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.feature-highlight i {
  color: #10b981;
}

/* Currency Selector */
.currency-selector {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
  margin-top: 1.25rem;
}

.currency-label {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  opacity: 0.75;
}

.currency-toggle {
  display: inline-flex;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  padding: 4px;
  gap: 4px;
}

.currency-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.78);
  padding: 0.5rem 1.25rem;
  font-weight: 600;
  font-size: 0.875rem;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.04em;
}

.currency-btn:hover:not(.active) {
  color: white;
  background: rgba(255, 255, 255, 0.07);
}

.currency-btn.active {
  background: white;
  color: #0a2540;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}

/* Pricing Section */
.pricing-section {
  padding: 80px 0;
  background: white;
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.pricing-card {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  border: 2px solid transparent;
  display: flex;
  flex-direction: column;
}

.pricing-card.featured {
  border-color: #1a7fd4;
  transform: scale(1.04);
  background: #0a2540;
  color: white;
}

.pricing-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.pricing-card.featured:hover {
  transform: scale(1.04) translateY(-2px);
}

.plan-header {
  text-align: center;
  margin-bottom: 1.75rem;
  position: relative;
}

.plan-tier-tag {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  background: rgba(26, 127, 212, 0.12);
  color: #1a7fd4;
  margin-bottom: 1rem;
}

.pricing-card.featured .plan-tier-tag {
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
}

.featured-badge {
  position: absolute;
  top: -45px;
  left: 50%;
  transform: translateX(-50%);
  background: #10b981;
  color: white;
  padding: 0.4rem 1.25rem;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  white-space: nowrap;
}

.plan-header h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.plan-price {
  margin-bottom: 1rem;
}

.currency {
  font-size: 1.2rem;
  vertical-align: top;
  margin-right: 0.125rem;
}

.amount {
  font-size: 3rem;
  font-weight: 700;
}

.amount-na {
  font-size: 1.4rem;
  font-weight: 600;
  opacity: 0.85;
}

.period {
  font-size: 1rem;
  opacity: 0.7;
}

.plan-discount {
  margin-top: 0.5rem;
  display: inline-block;
  background: #10b981;
  color: white;
  padding: 0.2rem 0.625rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.plan-description {
  opacity: 0.8;
  line-height: 1.5;
  font-size: 0.95rem;
}

.plan-features {
  margin-bottom: 1.75rem;
  flex-grow: 1;
}

.plan-features h4 {
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 1.5rem 0 0.875rem 0;
  color: #1a7fd4;
}

.plan-features h4:first-child {
  margin-top: 0;
}

.pricing-card.featured .plan-features h4 {
  color: #6cb2ee;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 0 0 0.5rem 0;
}

.feature-list li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 0.95rem;
}

.pricing-card.featured .feature-list li {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.feature-list li:last-child {
  border-bottom: none;
}

.feature-list i {
  color: #51cf66;
  font-size: 0.9rem;
  width: 20px;
  text-align: center;
}

.pricing-card.featured .feature-list i {
  color: #34d399;
}

.plan-action {
  text-align: center;
}

.plan-action .btn {
  width: 100%;
}

/* Buttons */
.btn {
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.975rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
}

.btn-primary {
  background-color: #1a7fd4;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0d6ebd;
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(26, 127, 212, 0.3);
}

.btn-outline {
  background: transparent;
  color: #1a7fd4;
  border: 2px solid #1a7fd4;
}

.btn-outline:hover {
  background: #1a7fd4;
  color: white;
}

.pricing-card.featured .btn-outline {
  color: white;
  border-color: white;
}

.pricing-card.featured .btn-outline:hover {
  background: white;
  color: #0a2540;
}

/* Skeleton */
.skeleton-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

.skeleton-header,
.skeleton-line,
.skeleton-price,
.skeleton-button {
  background: linear-gradient(90deg, #eef2f7 0%, #f8fafc 50%, #eef2f7 100%);
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.skeleton-header {
  height: 28px;
  width: 50%;
  margin-bottom: 1rem;
}

.skeleton-line {
  height: 14px;
  margin-bottom: 0.625rem;
}

.skeleton-line.short {
  width: 65%;
}

.skeleton-price {
  height: 56px;
  width: 70%;
  margin: 1rem 0 1.5rem;
}

.skeleton-button {
  height: 44px;
  width: 100%;
  margin-top: 1.25rem;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Error state */
.pricing-error {
  max-width: 480px;
  margin: 2rem auto;
  text-align: center;
  background: white;
  padding: 2.5rem 2rem;
  border-radius: 16px;
  border: 1px solid #fee2e2;
}

.pricing-error i {
  font-size: 2.5rem;
  color: #f59e0b;
  margin-bottom: 1rem;
}

.pricing-error h3 {
  margin: 0 0 0.5rem;
  color: #0a2540;
  font-size: 1.25rem;
}

.pricing-error p {
  color: #6b7280;
  margin-bottom: 1.5rem;
}

.pricing-error .btn {
  display: inline-flex;
  width: auto;
}

/* Enterprise Section */
.enterprise-section {
  padding: 80px 0;
  background: #0a2540;
  color: white;
}

.enterprise-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
}

.enterprise-info h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.enterprise-info p {
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 2rem;
}

.enterprise-features {
  display: grid;
  gap: 1.5rem;
}

.enterprise-feature {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.enterprise-feature i {
  font-size: 1.5rem;
  color: #1a7fd4;
  margin-top: 0.25rem;
}

.enterprise-feature h4 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.enterprise-feature p {
  opacity: 0.8;
  line-height: 1.5;
}

.contact-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 2.5rem;
  text-align: center;
  color: #111827;
}

.contact-card h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.contact-card p {
  opacity: 0.9;
  margin-bottom: 2rem;
}

.contact-info {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.contact-info p {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  opacity: 0.8;
}

/* FAQ Section */
.faq-section {
  padding: 80px 0;
  background: #f8fafc;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-header h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.section-header p {
  font-size: 1.1rem;
  color: #666;
}

.faq-grid {
  display: grid;
  gap: 1rem;
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.faq-question {
  padding: 1.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
}

.faq-question:hover {
  background: #f8fafc;
}

.faq-question h4 {
  font-weight: 600;
  margin: 0;
}

.faq-question i {
  transition: transform 0.3s ease;
  color: #1a7fd4;
}

.faq-question i.rotated {
  transform: rotate(180deg);
}

.faq-answer {
  padding: 0 1.5rem 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.faq-answer p {
  margin: 1rem 0 0 0;
  line-height: 1.6;
  color: #666;
}

/* Responsive */
@media (max-width: 1024px) {
  .enterprise-content {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  .pricing-grid {
    max-width: none;
  }
}

@media (max-width: 768px) {
  .pricing-header h1 {
    font-size: 2.5rem;
  }

  .pricing-features {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .pricing-grid {
    grid-template-columns: 1fr;
  }

  .pricing-card.featured {
    transform: none;
  }

  .pricing-card.featured:hover {
    transform: translateY(-2px);
  }
}

@media (max-width: 480px) {
  .pricing-header {
    padding: 60px 0;
  }

  .pricing-header h1 {
    font-size: 2rem;
  }

  .pricing-card {
    padding: 2rem;
  }

  .contact-card {
    padding: 2rem;
  }
}
</style>
